import React, { useState, useEffect } from 'react';
import { SpaceBetween, Box, Button, Header, Spinner, Alert, ToggleButton } from "@amzn/awsui-components-react";
import { Board, BoardItem } from "@amzn/awsui-board-components";
import ApplicationDetailsTile from './ApplicationDetailsTile';
import TeamDetailsTile from './TeamDetailsTile';
import OperationalDetailsTile from './OperationalDetailsTile';
import OtherDetailsTile from './OtherDetailsTile';
import MetaComponent from '../../../Ateam-BI/META/MetaComponent';
import { loadTileLayout, saveTileLayout } from '../../../utilities/TileLayoutPreferences';
import { createAuditLog } from '../Utils/AppActivityUtils';
import { APP_EDIT_ROLE_METRICS } from '../Constants';
import { ACTION_METHODS } from 'src/katalMetrics/metricsModels';
import { captureAppEditRoleMetric } from '../Utils/appEditRoleMetrics';

import './style.scss';

const TilesContainer = ({ 
  renderJsonEdited, 
  isEditable, 
  getValue, 
  updateValue, 
  getSelectFieldValues, 
  getMultiInputFieldValues, 
  getMultiSelectedOptions, 
  shouldShowMandatory, 
  mandatoryFieldListener, 
  postProcessTeamEmails, 
  accountingValidation, 
  suggestedLogins, 
  getSuggestedLoginsCaller, 
  aliasesLoading, 
  directReports, 
  getDirectReportsCaller, 
  directReportsLoading,
  canEdit,
  featuredPressed,
  setFeaturedPressed,
  markFeatured,
  id,
  isNewApp,
  isEditMode,
  dataFromMeta,
  userId,
  isSupportAdmin,
  // Limited-edit role (e.g. GRC control owner); may save the layout when role.canEditLayout
  appEditRole,
  // Additional data props for tiles
  accountingOrg,
  usecaseRunFrequency,
  usecaseType,
  financialRelevance,
  usecaseInputSourceTypes,
  usecaseAutomationTools,
  usecaseLedgerImpact,
  usecaseRegions
}) => {
  const [boardItems, setBoardItems] = useState([]);
  const [saveLayoutLoading, setSaveLayoutLoading] = useState(false);
  const [loadLayoutLoading, setLoadLayoutLoading] = useState(true);
  const [layoutError, setLayoutError] = useState(null);
  const [layoutSaveSuccess, setLayoutSaveSuccess] = useState(false);
  const [preference, setPreference] = useState(null);
  const [hasLayoutChanges, setHasLayoutChanges] = useState(false);

  // Initialize default layout
  const getDefaultItems = () => {
    return [
      {
        id: "application-details",
        columnSpan: 2,
        rowSpan: 5,
        columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
        data: {
          title: "Application Details",
          content: (
            <ApplicationDetailsTile
              renderJsonEdited={renderJsonEdited}
              isEditable={isEditable}
              getValue={getValue}
              updateValue={updateValue}
              getSelectFieldValues={getSelectFieldValues}
              getMultiInputFieldValues={getMultiInputFieldValues}
              getMultiSelectedOptions={getMultiSelectedOptions}
              shouldShowMandatory={shouldShowMandatory}
              mandatoryFieldListener={mandatoryFieldListener}
              canEdit={canEdit}
              featuredPressed={featuredPressed}
              setFeaturedPressed={setFeaturedPressed}
              markFeatured={markFeatured}
              id={id}
              isNewApp={isNewApp}
              isEditMode={isEditMode}
              accountingOrg={accountingOrg}
              usecaseRunFrequency={usecaseRunFrequency}
              usecaseType={usecaseType}
              financialRelevance={financialRelevance}
            />
          )
        }
      },
      {
        id: "team-details",
        columnSpan: 2,
        rowSpan: 4,
        columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
        data: {
          title: "Team Details",
          content: (
            <TeamDetailsTile
              renderJsonEdited={renderJsonEdited}
              isEditable={isEditable}
              getValue={getValue}
              updateValue={updateValue}
              getSelectFieldValues={getSelectFieldValues}
              getMultiInputFieldValues={getMultiInputFieldValues}
              shouldShowMandatory={shouldShowMandatory}
              postProcessTeamEmails={postProcessTeamEmails}
              accountingValidation={accountingValidation}
              suggestedLogins={suggestedLogins}
              getSuggestedLoginsCaller={getSuggestedLoginsCaller}
              aliasesLoading={aliasesLoading}
              directReports={directReports}
              getDirectReportsCaller={getDirectReportsCaller}
              directReportsLoading={directReportsLoading}
            />
          )
        }
      },
      {
        id: "operational-details",
        columnSpan: 12,
        rowSpan: 3,
        columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
        data: {
          title: "Operational Details",
          content: (
            <OperationalDetailsTile
              renderJsonEdited={renderJsonEdited}
              isEditable={isEditable}
              getValue={getValue}
              updateValue={updateValue}
              getSelectFieldValues={getSelectFieldValues}
              getMultiSelectedOptions={getMultiSelectedOptions}
              shouldShowMandatory={shouldShowMandatory}
              usecaseInputSourceTypes={usecaseInputSourceTypes}
              usecaseAutomationTools={usecaseAutomationTools}
            />
          )
        }
      },
      {
        id: "other-details",
        columnSpan: 12,
        rowSpan: 3,
        columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
        data: {
          title: "Other Details",
          content: (
            <OtherDetailsTile
              renderJsonEdited={renderJsonEdited}
              isEditable={isEditable}
              getValue={getValue}
              updateValue={updateValue}
              getSelectFieldValues={getSelectFieldValues}
              getMultiInputFieldValues={getMultiInputFieldValues}
              getMultiSelectedOptions={getMultiSelectedOptions}
              shouldShowMandatory={shouldShowMandatory}
              usecaseLedgerImpact={usecaseLedgerImpact}
              usecaseRegions={usecaseRegions}
            />
          )
        }
      }
    ];
  };

  // Load layout from DynamoDB on component mount
  useEffect(() => {
    const loadLayout = async () => {
      if (!userId) {
        setBoardItems(getDefaultItems());
        setLoadLayoutLoading(false);
        return;
      }

      setLoadLayoutLoading(true);
      setLayoutError(null);

      try {
        // Load from DynamoDB
        loadTileLayout(
          id,
          userId,
          (response) => {
            // Handle new response format: { layoutData, preferenceId, preference }
            const { layoutData, preferenceId: loadedPreferenceId, preference: loadedPreference } = response || {};
            
            // Store the complete preference object for future saves
            setPreference(loadedPreference);
            
            if (layoutData && Array.isArray(layoutData)) {
              // Get default items with content
              const defaultItems = getDefaultItems();
              
              // Add Meta component to default items if available
              if (dataFromMeta) {
                defaultItems.push({
                  id: "meta-details",
                  columnSpan: 12,
                  rowSpan: 1,
                  columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
                  data: {
                    title: "Meta Information",
                    content: <MetaComponent data={dataFromMeta} />
                  }
                });
              }

              // Build items in the SAVED ORDER, not default order
              const itemsWithSavedLayout = layoutData.map(savedItem => {
                const defaultItem = defaultItems.find(item => item.id === savedItem.id);
                if (defaultItem) {
                  return {
                    ...defaultItem,
                    columnSpan: savedItem.columnSpan,
                    rowSpan: savedItem.rowSpan,
                    columnOffset: savedItem.columnOffset
                  };
                }
                return null;
              }).filter(item => item !== null);

              // Add any items that exist in default but not in saved layout
              const savedIds = layoutData.map(item => item.id);
              const missingItems = defaultItems.filter(item => !savedIds.includes(item.id));
              
              setBoardItems([...itemsWithSavedLayout, ...missingItems]);
            } else {
              // No saved layout, use default
              const defaultItems = getDefaultItems();
              if (dataFromMeta) {
                defaultItems.push({
                  id: "meta-details",
                  columnSpan: 12,
                  rowSpan: 1,
                  columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
                  data: {
                    title: "Meta Information",
                    content: <MetaComponent data={dataFromMeta} />
                  }
                });
              }
              setBoardItems(defaultItems);
            }
            setLoadLayoutLoading(false);
          },
          (error) => {
            setLayoutError('Failed to load saved layout. Using default layout.');
            const defaultItems = getDefaultItems();
            if (dataFromMeta) {
              defaultItems.push({
                id: "meta-details",
                columnSpan: 12,
                rowSpan: 1,
                columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
                data: {
                  title: "Meta Information",
                  content: <MetaComponent data={dataFromMeta} />
                }
              });
            }
            setBoardItems(defaultItems);
            setLoadLayoutLoading(false);
          }
        );
      } catch (error) {
        setLayoutError('Error loading layout. Using default layout.');
        const defaultItems = getDefaultItems();
        if (dataFromMeta) {
          defaultItems.push({
            id: "meta-details",
            columnSpan: 12,
            rowSpan: 1,
            columnOffset: { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 },
            data: {
              title: "Meta Information",
              content: <MetaComponent data={dataFromMeta} />
            }
          });
        }
        setBoardItems(defaultItems);
        setLoadLayoutLoading(false);
      }
    };

    loadLayout();
  }, [id, userId, dataFromMeta]);

  // Update board items when other props change (but keep layout)
  useEffect(() => {
    if (boardItems.length === 0) return;

    const updatedItems = boardItems.map(item => {
      // Keep the same layout but update content
      if (item.id === "application-details") {
        return {
          ...item,
          data: {
            ...item.data,
            content: (
              <ApplicationDetailsTile
                renderJsonEdited={renderJsonEdited}
                isEditable={isEditable}
                getValue={getValue}
                updateValue={updateValue}
                getSelectFieldValues={getSelectFieldValues}
                getMultiInputFieldValues={getMultiInputFieldValues}
                getMultiSelectedOptions={getMultiSelectedOptions}
                shouldShowMandatory={shouldShowMandatory}
                mandatoryFieldListener={mandatoryFieldListener}
                canEdit={canEdit}
                featuredPressed={featuredPressed}
                setFeaturedPressed={setFeaturedPressed}
                markFeatured={markFeatured}
                id={id}
                isNewApp={isNewApp}
                isEditMode={isEditMode}
                accountingOrg={accountingOrg}
                usecaseRunFrequency={usecaseRunFrequency}
                usecaseType={usecaseType}
                financialRelevance={financialRelevance}
              />
            )
          }
        };
      } else if (item.id === "team-details") {
        return {
          ...item,
          data: {
            ...item.data,
            content: (
              <TeamDetailsTile
                renderJsonEdited={renderJsonEdited}
                isEditable={isEditable}
                getValue={getValue}
                updateValue={updateValue}
                getSelectFieldValues={getSelectFieldValues}
                getMultiInputFieldValues={getMultiInputFieldValues}
                shouldShowMandatory={shouldShowMandatory}
                postProcessTeamEmails={postProcessTeamEmails}
                accountingValidation={accountingValidation}
                suggestedLogins={suggestedLogins}
                getSuggestedLoginsCaller={getSuggestedLoginsCaller}
                aliasesLoading={aliasesLoading}
                directReports={directReports}
                getDirectReportsCaller={getDirectReportsCaller}
                directReportsLoading={directReportsLoading}
              />
            )
          }
        };
      } else if (item.id === "operational-details") {
        return {
          ...item,
          data: {
            ...item.data,
            content: (
              <OperationalDetailsTile
                renderJsonEdited={renderJsonEdited}
                isEditable={isEditable}
                getValue={getValue}
                updateValue={updateValue}
                getSelectFieldValues={getSelectFieldValues}
                getMultiSelectedOptions={getMultiSelectedOptions}
                shouldShowMandatory={shouldShowMandatory}
                usecaseInputSourceTypes={usecaseInputSourceTypes}
                usecaseAutomationTools={usecaseAutomationTools}
              />
            )
          }
        };
      } else if (item.id === "other-details") {
        return {
          ...item,
          data: {
            ...item.data,
            content: (
              <OtherDetailsTile
                renderJsonEdited={renderJsonEdited}
                isEditable={isEditable}
                getValue={getValue}
                updateValue={updateValue}
                getSelectFieldValues={getSelectFieldValues}
                getMultiInputFieldValues={getMultiInputFieldValues}
                getMultiSelectedOptions={getMultiSelectedOptions}
                shouldShowMandatory={shouldShowMandatory}
                usecaseLedgerImpact={usecaseLedgerImpact}
                usecaseRegions={usecaseRegions}
              />
            )
          }
        };
      } else if (item.id === "meta-details" && dataFromMeta) {
        return {
          ...item,
          data: {
            ...item.data,
            content: <MetaComponent data={dataFromMeta} />
          }
        };
      }
      return item;
    });

    setBoardItems(updatedItems);
  }, [
    renderJsonEdited, isEditable, getValue, updateValue, getSelectFieldValues, 
    getMultiInputFieldValues, getMultiSelectedOptions, shouldShowMandatory, 
    mandatoryFieldListener, postProcessTeamEmails, accountingValidation, 
    suggestedLogins, getSuggestedLoginsCaller, aliasesLoading, directReports, 
    getDirectReportsCaller, directReportsLoading, canEdit, featuredPressed, 
    setFeaturedPressed, markFeatured, isNewApp, isEditMode,
    accountingOrg, usecaseRunFrequency, usecaseType, financialRelevance,
    usecaseInputSourceTypes, usecaseAutomationTools, usecaseLedgerImpact, usecaseRegions
  ]);

  const handleItemsChange = (event) => {
    setBoardItems(event.detail.items);
    setHasLayoutChanges(true); 
  };

  const saveLayout = () => {
    if (!userId) {
      setLayoutError('Cannot save layout: User not identified');
      return;
    }

    // Metrics only for the limited-edit role path; support admins are not a role holder here
    const layoutRole = isSupportAdmin ? null : appEditRole;
    captureAppEditRoleMetric(ACTION_METHODS.BUTTON_CLICK, layoutRole, APP_EDIT_ROLE_METRICS.SAVE_LAYOUT);
    setSaveLayoutLoading(true);
    setLayoutError(null);
    setLayoutSaveSuccess(false);

    // Save layout to DynamoDB
    const layoutData = boardItems.map(item => ({
      id: item.id,
      columnSpan: item.columnSpan,
      rowSpan: item.rowSpan,
      columnOffset: item.columnOffset
    }));
 const beforeState = preference?.information || null;

    saveTileLayout(
      id,
      userId,
      layoutData,
      preference, 
      (response) => {
        const updatedPreference = response?.preference || response?.data?.createPreference || response?.data?.updatePreference;
    
        if (updatedPreference) {
          setPreference(updatedPreference);
        }
        
        setSaveLayoutLoading(false);
        setHasLayoutChanges(false); 
    try {
          const timestamp = Date.now();
          const pk = `APP#${id}`; 
          const sk = `${timestamp}`; 
          const action = "SAVE_TILE_LAYOUT";
          const actor = userId;
          const comments = `User saved tile layout configuration for app ${id}`;
      const beforeData = beforeState || [];
          const afterData = layoutData;
          
          createAuditLog(
            pk,
            sk,
            comments,
            beforeData,
            JSON.stringify(afterData),
            action,
            actor,
          );
        } catch (auditError) {
          console.error('Error creating audit log:', auditError);
        }
        setLayoutSaveSuccess(true);
        captureAppEditRoleMetric(ACTION_METHODS.SUCCESS, layoutRole, APP_EDIT_ROLE_METRICS.SAVE_LAYOUT);
        // Clear success message after 3 seconds
        setTimeout(() => {
          setLayoutSaveSuccess(false);
        }, 5000);
      },
      (error) => {
        captureAppEditRoleMetric(ACTION_METHODS.FAILURE, layoutRole, APP_EDIT_ROLE_METRICS.SAVE_LAYOUT);
        setLayoutError('Failed to save layout. Please try again.');
        setSaveLayoutLoading(false);
      }
    );
  };

  if (loadLayoutLoading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <Box variant="p" color="text-body-secondary" margin={{ top: "s" }}>
          Loading layout preferences...
        </Box>
      </Box>
    );
  }

  return (
    <SpaceBetween size="l">
      {layoutError && (
        <Alert
          type="warning"
          dismissible
          onDismiss={() => setLayoutError(null)}
        >
          {layoutError}
        </Alert>
      )}
      {layoutSaveSuccess && (
        <Alert
          type="success"
          dismissible
          onDismiss={() => setLayoutSaveSuccess(false)}
        >
          Layout saved successfully!
        </Alert>
      )}
      <div className={isNewApp ? 'board-disabled' : ''}>
        <Board
          renderItem={item => (
          <BoardItem
            header={
              item.id === "application-details" && !isNewApp && !isEditMode ? (
                <Box>
                  <SpaceBetween direction="horizontal" size="l" alignItems="center">
                    <Header variant="h3">{item.data.title}</Header>
                    <ToggleButton
                      onChange={({ detail }) => {
                        setFeaturedPressed(detail.pressed);
                        markFeatured(id);
                      }}
                      pressed={featuredPressed}
                      iconName="heart"
                      pressedIconName="heart-filled"
                      disabled={!canEdit}
                    >
                      {featuredPressed ? 'Unfeature app' : 'Feature app'}
                    </ToggleButton>
                  </SpaceBetween>
                </Box>
              ) : (
                <Header>{item.data.title}</Header>
              )
            }
            i18nStrings={{
              dragHandleAriaLabel: "Drag handle",
              dragHandleAriaDescription:
                "Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to discard.",
              resizeHandleAriaLabel: "Resize handle",
              resizeHandleAriaDescription:
                "Use Space or Enter to activate resize, arrow keys to move, Space or Enter to submit, or Escape to discard."
            }}
          >
            {item.data.content}
          </BoardItem>
        )}
        onItemsChange={isNewApp ? () => {} : handleItemsChange}
        items={boardItems}
        i18nStrings={(() => {
          function createAnnouncement(operationAnnouncement, conflicts, disturbed) {
            const conflictsAnnouncement =
              conflicts.length > 0
                ? `Conflicts with ${conflicts.map(c => c.data.title).join(", ")}.`
                : "";
            const disturbedAnnouncement =
              disturbed.length > 0 ? `Disturbed ${disturbed.length} items.` : "";
            return [operationAnnouncement, conflictsAnnouncement, disturbedAnnouncement]
              .filter(Boolean)
              .join(" ");
          }
          return {
            liveAnnouncementDndStarted: operationType =>
              operationType === "resize" ? "Resizing" : "Dragging",
            liveAnnouncementDndItemReordered: operation => {
              const columns = `column ${operation.placement.x + 1}`;
              const rows = `row ${operation.placement.y + 1}`;
              return createAnnouncement(
                `Item moved to ${operation.direction === "horizontal" ? columns : rows}.`,
                operation.conflicts,
                operation.disturbed
              );
            },
            liveAnnouncementDndItemResized: operation => {
              const columnsConstraint = operation.isMinimalColumnsReached ? " (minimal)" : "";
              const rowsConstraint = operation.isMinimalRowsReached ? " (minimal)" : "";
              const sizeAnnouncement =
                operation.direction === "horizontal"
                  ? `columns ${operation.placement.width}${columnsConstraint}`
                  : `rows ${operation.placement.height}${rowsConstraint}`;
              return createAnnouncement(
                `Item resized to ${sizeAnnouncement}.`,
                operation.conflicts,
                operation.disturbed
              );
            },
            liveAnnouncementDndItemInserted: operation => {
              const columns = `column ${operation.placement.x + 1}`;
              const rows = `row ${operation.placement.y + 1}`;
              return createAnnouncement(
                `Item inserted to ${columns}, ${rows}.`,
                operation.conflicts,
                operation.disturbed
              );
            },
            liveAnnouncementDndCommitted: operationType => `${operationType} committed`,
            liveAnnouncementDndDiscarded: operationType => `${operationType} discarded`,
            liveAnnouncementItemRemoved: op =>
              createAnnouncement(`Removed item ${op.item.data.title}.`, [], op.disturbed),
            navigationAriaLabel: "Board navigation",
            navigationAriaDescription: "Click on non-empty item to move focus over",
            navigationItemAriaLabel: item => (item ? item.data.title : "Empty")
          };
        })()}
        empty={
          <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <Box variant="strong" color="inherit">
                No tiles available
              </Box>
            </SpaceBetween>
          </Box>
        }
      />
      </div>
      {(isSupportAdmin || !!appEditRole?.canEditLayout) && !isEditMode &&  (
        <Box float="right">
          <Button 
            variant="primary" 
            onClick={saveLayout} 
            loading={saveLayoutLoading}
            iconName="settings"
            disabled={!userId || !hasLayoutChanges}
          >
            Save Layout
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
};

export default TilesContainer;
